import axios from "axios";
import { sendVoucherEmailAttempt } from "./voucherEmailAttempt";
import { store } from "../store";
import config from "../config";
import authHeader from "./auth-header";
import unroutedEndpoint from "./unroutedEndpoint";
import { encodeQueryValue } from "./queryString";

const API_URL = config.API_URL;
const pendingInvoiceEmailKeys = new Map();
const pendingSaleKeys = new Map();
const recurringResolutionKeys = new Map();

const idempotencyFingerprint = (value) => {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(idempotencyFingerprint);
  return Object.keys(value)
    .filter((key) => !["idempotency_key", "idempotencyKey", "paymentIntent"].includes(key))
    .sort()
    .reduce((result, key) => {
      result[key] = idempotencyFingerprint(value[key]);
      return result;
    }, {});
};

const generatedKey = (prefix = "sale") => {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const recurringDepositContext = (values) => axios.post(
  API_URL + "afterlogin/sale/recurring-split/context", values, { headers: authHeader() },
);

const resolveRecurringDeposit = (values) => {
  const fingerprint = JSON.stringify([store.getState().auth?.user?.auth_key, idempotencyFingerprint(values)]);
  const key = recurringResolutionKeys.get(fingerprint) || generatedKey("deposit-resolution");
  recurringResolutionKeys.set(fingerprint, key);
  // Retain a successful key too: refreshing the target can fail after commit.
  // The same explicit resolution must replay rather than allocate credit twice.
  return axios.post(API_URL + "afterlogin/sale/recurring-split/resolve", values,
    { headers: { ...authHeader(), "Idempotency-Key": key } });
};

const positiveInteger = (value) => {
  const integer = Number(value);
  return Number.isInteger(integer) && integer > 0 ? integer : null;
};

const legacyPage = (url) => {
  if (typeof url !== "string" || !url) return null;

  try {
    return positiveInteger(new URL(url, "https://sale-list.invalid").searchParams.get("page"));
  } catch (_error) {
    return null;
  }
};

const invoiceListRequest = (values = {}) => {
  const page = positiveInteger(values.page) || legacyPage(values.next_page_url) || 1;
  const requestedLimit = positiveInteger(values.limit);
  const limit = requestedLimit && requestedLimit <= 100 ? requestedLimit : 10;

  return {
    ...(values.date_basis ? { date_basis: values.date_basis } : {}),
    apicall: "suggetion",
    client_id: values.client_id || "",
    daterange: values.daterange || "",
    id: values.id || "",
    limit,
    page,
    q: typeof values.q === "string" ? values.q : "",
  };
};

const create = (values) => {
  // Formik keeps included-product choices next to cart. The checkout contract
  // prices and consumes selectors from inside cart; preserve legacy callers
  // that already send them there and never mutate the caller's state.
  const cart = values.cart && typeof values.cart === "object" ? { ...values.cart } : {};
  ["membership_product_usable_credit_data", "subscription_product_usable_credit_data"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) cart[key] = values[key];
  });
  values = { ...values, cart };
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    if (["idempotency_key", "idempotencyKey"].includes(value)) continue;
    if (value === "payment_allocations" &&
        (values[value] == null || (Array.isArray(values[value]) && values[value].length === 0))) continue;
    if (["cart", "payment_allocations", "membership_service_usable_credit_data", "singlesSubServiceAppointments", "membership_product_usable_credit_data", "subscription_product_usable_credit_data"].includes(value)
        && values[value]
        && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }

  const action = "afterlogin/sale/store";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  if(localStorage.getItem('hasAutomationAccess')==1){
    formData.append("automationProcess", 1);
  }
  const requestedKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitKey = typeof requestedKey === "string" && requestedKey.trim() ? requestedKey.trim() : null;
  const fingerprint = explicitKey ? null : JSON.stringify(idempotencyFingerprint(values || {}));
  const idempotencyKey = explicitKey || pendingSaleKeys.get(fingerprint) || generatedKey();
  if (!explicitKey) pendingSaleKeys.set(fingerprint, idempotencyKey);
  const request = axios.post(API_URL + action, formData, {
    headers: {
      ...authHeader({ contentType: "multipart/form-data" }),
      "Idempotency-Key": idempotencyKey,
    },
  });
  if (explicitKey) return request;
  return Promise.resolve(request).then((response) => {
    pendingSaleKeys.delete(fingerprint);
    return response;
  });
};

const update = (values) => {
  const editFields = ["discount_code", "preview", "settle_balance", "line_items", "discount_minor", "tax_minor", "notes", "paidby", "paidby_status"];
  const payload = { id: values.id, ...(values.invoicedate !== undefined ? { invoicedate: values.invoicedate } : {}) };
  editFields.forEach((field) => {
    if (values[field] !== undefined) payload[field] = values[field];
  });
  return axios.post(
    API_URL + "afterlogin/sale/update",
    payload,
    { headers: authHeader() },
  );
};

const voidInvoice = ({ id }) => axios.post(
  API_URL + "afterlogin/sale/deactivateinvoice",
  { id },
  { headers: authHeader() },
);

const view = () => {
  // Flask exposes sale reads by resource (invoice, services, products, vouchers); the generic sale view was never implemented.
  return unroutedEndpoint(
    "afterlogin/sale/view",
    "afterlogin/sale/invoice"
  );
};

const invoiceview = (values) => {
  return axios.post(
    API_URL + "afterlogin/sale/invoice",
    invoiceListRequest(values),
    { headers: authHeader() },
  );
};

const createinvoiceview = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const page = positiveInteger(values?.page) || legacyPage(values?.next_page_url) || 1;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const formName = values && values.formName ? values.formName : "";
  const clickEvent = values && values.clickEvent ? values.clickEvent : "";
  const id = values && values.id ? values.id : "";
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if (values) {
    if (values.id) {
      pagination = false;
    }
    if (values.option) {
      pagination = false;
    }
    if (values.pagination) {
      pagination = values.pagination;
    }
    if(clickEvent){
      pagination = false;
    }
  }
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = page ? `afterlogin/sale/createinvoice?page=${page}&${sortstring}&q=${encodedQ}` : `afterlogin/sale/createinvoice?${sortstring}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    id:id,
    pagination: pagination, //true or false
    id: values && values.id ? values.id : "",
    client_id: values && values.client_id ? values.client_id : "",
    daterange: values && values.daterange ? values.daterange : "",
    result: result, //business_name,owner_name
    formName: formName, //business_name,owner_name
    option: values && values.option ? values.option : "",
    singlestaffaccess : values && values.singlestaffaccess ? values.singlestaffaccess : "",
    apicall: "suggetion",
    page,
    q,
  };
  return axios.post(next_page_url ? `${next_page_url}&${sortstring}&q=${encodedQ}` : API_URL + action, data, { headers: authHeader() });
};

const deleted = () => {
  // Flask exposes sale removal as sale/hideinvoice; sale/delete was never implemented.
  return unroutedEndpoint(
    "afterlogin/sale/delete/",
    "afterlogin/sale/hideinvoice"
  );
};

const suggetionlist = () => {
  // Flask exposes sale reads by resource (invoice, services, products, vouchers); the generic sale view was never implemented.
  return unroutedEndpoint(
    "afterlogin/sale/view",
    "afterlogin/sale/invoice"
  );
};

const services = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  let service_id = values && values.service_id ? values.service_id : "";
  let staff = values && values.staff ? values.staff : "";
  let gprice = values && values.gprice ? values.gprice : "";
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = page ? `afterlogin/sale/services?page=${page}&service_id=${service_id}&q=${encodedQ}` : `afterlogin/sale/services?service_id=${service_id}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    staff: staff,
    gprice: gprice,
  };
  if (service_id !== "") {
    data.service_id = service_id;
  }
  return axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader() });
};

const requestProducts = (values, endpoint) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const requestedPage = positiveInteger(values && values.page) || legacyPage(values && values.next_page_url);
  const requestedLimit = positiveInteger(values && values.limit);
  const limit = requestedLimit && requestedLimit <= 100 ? requestedLimit : 100;
  let product_id = values && values.product_id ? values.product_id : "";
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const path = endpoint === "invoiceproducts"
    ? "afterlogin/sale/invoiceproducts"
    : "afterlogin/sale/products";
  const action = requestedPage ? `${path}?page=${requestedPage}&product_id=${product_id}&q=${encodedQ}` : `${path}?product_id=${product_id}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    limit: limit,
    page: requestedPage || 1,
    qty: values && values.qty ? values.qty : 1,
    page_type : values && values.page_type ? values.page_type : "",
  };
  if (product_id) data.product_id = product_id;
  if (q) data.q = q;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const vouchers = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const voucherValues = values || {};
  const page = positiveInteger(voucherValues.page) || legacyPage(voucherValues.next_page_url) || 1;
  const requestedLimit = positiveInteger(voucherValues.limit);
  const limit = requestedLimit && requestedLimit <= 100 ? requestedLimit : 10;
  let voucher_id = voucherValues.voucher_id ? voucherValues.voucher_id : "";
  let q = voucherValues.q ? voucherValues.q : "";
  const encodedQ = encodeQueryValue(q);
  const hasVoucherId = voucher_id !== "";
  const action = hasVoucherId
    ? `afterlogin/sale/vouchers?voucher_id=${voucher_id}&q=${encodedQ}`
    : `afterlogin/sale/vouchers?q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    voucher_to: voucherValues,
  };
  if (hasVoucherId) data.voucher_id = voucher_id;
  else {
    data.page = page;
    data.limit = limit;
  }
  if (q) data.q = q;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const membership = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const requestedPage = positiveInteger(values && values.page) || legacyPage(values && values.next_page_url);
  const requestedLimit = positiveInteger(values && values.limit);
  const limit = requestedLimit && requestedLimit <= 100 ? requestedLimit : 100;
  const membership_id = positiveInteger(values && values.membership_id);
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = requestedPage ? `afterlogin/sale/membership?page=${requestedPage}&membership_id=${membership_id || ""}&q=${encodedQ}` : `afterlogin/sale/membership?membership_id=${membership_id || ""}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    limit: limit,
    page: requestedPage || 1,
  };
  if (membership_id) data.membership_id = membership_id;
  if (q) data.q = q;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const subscription = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const requestedPage = positiveInteger(values && values.page) || legacyPage(values && values.next_page_url);
  const requestedLimit = positiveInteger(values && values.limit);
  const limit = requestedLimit && requestedLimit <= 100 ? requestedLimit : 100;
  let subscription_id = values && values.subscription_id ? values.subscription_id : "";
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = requestedPage ? `afterlogin/sale/subscription?page=${requestedPage}&subscription_id=${subscription_id}&q=${encodedQ}` : `afterlogin/sale/subscription?subscription_id=${subscription_id}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    limit: limit,
    page: requestedPage || 1,
  };
  if (q) data.q = q;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const clientsuggetionlist = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = page ? `afterlogin/client/view?page=${page}&q=${encodedQ}` : `afterlogin/client/view?q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    role_id: 6,
    pagination: true, //true or false
    id: values && values.id ? values.id : "",
    apicall: "suggetion",
  };
  return axios.post(next_page_url ? `${next_page_url}&q=${encodedQ}` : API_URL + action, data, { headers: authHeader() });
};

const sendEmailInvoice = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    if (["idempotency_key", "idempotencyKey"].includes(value)) continue;
    formData.append(value, values[value]);
  }
  const action = "afterlogin/sale/sendEmailInvoice";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  formData.append("formName", values?.fomName);
  const requestedKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitKey = typeof requestedKey === "string" && requestedKey.trim() ? requestedKey.trim() : null;
  const fingerprint = explicitKey ? null : JSON.stringify(idempotencyFingerprint(values || {}));
  const idempotencyKey = explicitKey || pendingInvoiceEmailKeys.get(fingerprint) || generatedKey("invoice-email");
  if (!explicitKey) pendingInvoiceEmailKeys.set(fingerprint, idempotencyKey);
  const request = axios.post(API_URL + action, formData, {
    headers: {
      ...authHeader({ contentType: "multipart/form-data" }),
      "Idempotency-Key": idempotencyKey,
    },
  });
  if (explicitKey) return request;
  return Promise.resolve(request).then((response) => {
    pendingInvoiceEmailKeys.delete(fingerprint);
    return response;
  });
};
const voucherapply = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();

  formData.append("code", String(values?.code || "").trim());
  formData.append(
    "service_ids",
    JSON.stringify(Array.isArray(values?.service_ids) ? values.service_ids : []),
  );

  const action = "afterlogin/sale/voucherapply";
  formData.append("auth_key", auth_key);
  formData.append("action", action);

  return axios.post(API_URL + action, formData, { headers: authHeader({ contentType: "multipart/form-data" }) });
};

const discountquote = (values = {}) => {
  const action = "afterlogin/sale/discountcodeapply";
  return axios.post(
    API_URL + action,
    {
      code: String(values.code || "").trim().toUpperCase(),
      client_id: values.client_id,
      cart: values.cart && typeof values.cart === "object" ? values.cart : {},
      paidby: values.paidby,
      ...(Array.isArray(values.payment_allocations) && values.payment_allocations.length > 0
        ? { payment_allocations: values.payment_allocations } : {}),
      voucher_to_id: values.voucher_to_id,
      singlesSubServiceAppointments: Array.isArray(values.singlesSubServiceAppointments)
        ? values.singlesSubServiceAppointments
        : [],
      membership_service_usable_credit_data: Array.isArray(values.membership_service_usable_credit_data)
        ? values.membership_service_usable_credit_data
        : [],
    },
    { headers: authHeader() },
  );
};

const voucheroptions = (values = {}) => {
  const action = "afterlogin/sale/voucheroptions";
  return axios.post(
    API_URL + action,
    {
      client_id: values.client_id,
      service_ids: Array.isArray(values.service_ids) ? values.service_ids : [],
    },
    { headers: authHeader() },
  );
};

const benefitoptions = (values = {}, options = {}) => {
  const action = "afterlogin/sale/benefitoptions";
  const cart = ["appointments", "products", "services", "membership", "subscription", "vouchers"].reduce((result, key) => {
    const rows = Array.isArray(values.cart && values.cart[key])
      ? values.cart[key].map((item) => ({ id: Number(item.id), quantity: Number(item.quantity || item.qty || 1) }))
        .filter((item) => Number.isInteger(item.id) && item.id > 0 && Number.isInteger(item.quantity) && item.quantity > 0)
      : [];
    if (rows.length) result[key] = rows;
    return result;
  }, {});
  const selectedBenefits = Array.isArray(values.selected_benefits) ? values.selected_benefits.map((item) => {
    const benefit = { kind: item.kind };
    ["entitlement_id", "voucher_to_id", "service_id", "product_id", "quantity"].forEach((key) => {
      const numericValue = Number(item[key]);
      if (Number.isInteger(numericValue) && numericValue > 0) benefit[key] = numericValue;
    });
    return benefit;
  }) : [];
  const payload = {
    client_id: values.client_id,
    cart,
  };
  if (values.discount_code) payload.discount_code = values.discount_code;
  if (selectedBenefits.length) payload.selected_benefits = selectedBenefits;
  return axios.post(
    API_URL + action,
    payload,
    { headers: authHeader(), ...(options.signal ? { signal: options.signal } : {}) },
  );
};

const returnpayment = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    formData.append(value, values[value]);
  }
  const action = "afterlogin/sale/returnpayment";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader({ contentType: "multipart/form-data" }) });
};

const sendemailvoucher = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    formData.append(value, values[value]);
  }
  const action = "afterlogin/sale/sendemailvoucher";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return sendVoucherEmailAttempt({ auth, endpoint: API_URL + action, values,
    send: key => axios.post(API_URL + action, formData, { headers: {
      ...authHeader({ contentType: "multipart/form-data" }), "Idempotency-Key": key,
    } }),
  });
};

const saleservicesmanage = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    if (["cart", "client"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = "afterlogin/sale/servicesmanage";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader({ contentType: "multipart/form-data" }) });
};



const saledetails = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const formName = values && values.formName ? values.formName : "";
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if (values) {
    if (values.id) {
      pagination = false;
    }
    if (values.option) {
      pagination = false;
    }
    if (values.pagination) {
      pagination = values.pagination;
    }
  }
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = page ? `afterlogin/sale/invoice?page=${page}&${sortstring}&q=${encodedQ}` : `afterlogin/sale/invoice?${sortstring}&q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: pagination, //true or false
    id: values && values.id ? values.id : "",
    singlestaffaccess : values && values.singlestaffaccess ? values.singlestaffaccess : "",
    client_id: values && values.client_id ? values.client_id : "",
    daterange: values && values.daterange ? values.daterange : "",
    result: result, //business_name,owner_name
    formName: formName, //business_name,owner_name
    option: values && values.option ? values.option : "",
    apicall: "suggetion",
  };
  return axios.post(next_page_url ? `${next_page_url}&${sortstring}&q=${encodedQ}` : API_URL + action, data, { headers: authHeader() });
};

const productstafflist = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  void (values && values.dropdown ? values.dropdown : "");
  const result = values && values.result ? values.result : "";
  void (values && values.product_id ? values.product_id : "");
  const pagetype = values && values.pagetype ? values.pagetype : "";
  const action = `afterlogin/staff/view`;

  const data = {
    auth_key: auth_key,
    action: action,
    pagination: false,
    id: "",
    result: result, //business_name,owner_name
    pagetype: pagetype,
    option: values && values.option ? values.option : "",
    page_name: values && values.page_name ? values.page_name : "",
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const subscriptionstafflist = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  void (values && values.dropdown ? values.dropdown : "");
  const result = values && values.result ? values.result : "";
  void (values && values.product_id ? values.product_id : "");
  const pagetype = values && values.pagetype ? values.pagetype : "";
  const action = `afterlogin/staff/view`;

  const data = {
    auth_key: auth_key,
    action: action,
    pagination: false,
    id: "",
    result: result, //business_name,owner_name
    pagetype: pagetype,
    option: values && values.option ? values.option : "",
    page_name: values && values.page_name ? values.page_name : "",
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const saleApiController = {
  recurringDepositContext,
  resolveRecurringDeposit,
  create,
  saledetails,
  update,
  voidInvoice,
  view,
  deleted,
  suggetionlist,
  services,
  products: (values) => requestProducts(values, "products"),
  invoiceServices: () => axios.post(API_URL + "afterlogin/sale/invoiceservices", {}, { headers: authHeader() }),
  invoiceProducts: (values) => requestProducts(values, "invoiceproducts"),
  vouchers,
  membership,
  subscription,
  invoiceview,
  createinvoiceview,
  clientsuggetionlist,
  sendEmailInvoice,
  voucherapply,
  discountquote,
  voucheroptions,
  benefitoptions,
  returnpayment,
  sendemailvoucher,
  saleservicesmanage,
  productstafflist,
  subscriptionstafflist
};
export default saleApiController;
